/**
 * The m-line layout a fresh RTCPeerConnection has to repeat before a connected remote accepts its
 * offer (AVS-4484).
 *
 * A remote that is already in a session refuses an offer whose m-line count, order or mids differ
 * from that session ("The order of m-lines in subsequent offer doesn't match order from previous
 * offer/answer"). A fresh pc numbers its sections 0..n-1 in creation order, so it can repeat a
 * layout only when the remote's mids are exactly that run, in order, and no section was rejected or
 * is not media. Anything else cannot be mirrored and the caller must leave the link alone.
 *
 * Zero imports, like the other module-level helpers the VOIP core depends on.
 */

/** The most sections a mirrored offer is allowed to carry. A VOIP link never has more than a few. */
const MAX_MIRRORED_SECTIONS = 8;

export type TMirroredMlineKind = 'audio' | 'video';

/**
 * The ordered media kinds of `sdp`, or `null` when its layout cannot be mirrored.
 *
 * @param sdp - a session description, normally the pc's `currentRemoteDescription.sdp`
 * @returns {TMirroredMlineKind[] | null} one kind per section, in m-line order
 */
export function mirrorableMlines(sdp: unknown): TMirroredMlineKind[] | null {
  if (typeof sdp !== 'string' || sdp === '') return null;

  const kinds: TMirroredMlineKind[] = [];
  const mids: Array<string | undefined> = [];

  for (const rawLine of sdp.split('\n')) {
    const line = rawLine.trim();
    if (line.startsWith('m=')) {
      const [kind, port] = line.slice(2).split(' ');
      // A port of 0 is a rejected (stopped) section, and a data channel is not media: neither can be
      // repeated by a fresh pc.
      if ((kind !== 'audio' && kind !== 'video') || port === '0') return null;
      kinds.push(kind);
      mids.push(undefined);
    } else if (line.startsWith('a=mid:') && kinds.length > 0) {
      mids[kinds.length - 1] = line.slice('a=mid:'.length);
    }
  }

  if (kinds.length === 0 || kinds.length > MAX_MIRRORED_SECTIONS) return null;
  if (!mids.every((mid, index) => mid === String(index))) return null;

  return kinds;
}
